import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@material-ui/core";
import {
  HaklaiStyledHeaderCell as StyledHeaderCell,
  HaklaiStyledNameHeaderCell as StyledNameHeaderCell
} from "../../shared/table/haklaiTableShared";
import {
  ACTION_COLUMN_WIDTH,
  ADD_ROW_COLUMN_WIDTH,
  DEPRECIATION_COLUMN_WIDTH,
  PTOR_COLUMN_WIDTH,
  PROPERTY_PART_NAME_COLUMN_WIDTH,
  READ_ONLY_VALUE_COLUMN_WIDTH,
  STANDARD_VALUE_COLUMN_WIDTH,
  StyledGroupCell
} from "../haklaiPropertyPartsStyles";
import {
  HAKLAI_PROPERTY_PART_LABELS,
  HAKLAI_PROPERTY_PART_TOOLTIPS
} from "../haklaiPropertyPartsTexts";

const FLOATING_TOOLBAR_SAFE_GUTTER = 48;
const PROPERTY_PARTS_TABLE_BASE_MIN_WIDTH = PROPERTY_PART_NAME_COLUMN_WIDTH
  + ADD_ROW_COLUMN_WIDTH
  + STANDARD_VALUE_COLUMN_WIDTH
  + PTOR_COLUMN_WIDTH
  + ACTION_COLUMN_WIDTH;

const getZabanColumnWidth = (hasPre1997ConstructionQuestion) => (
  hasPre1997ConstructionQuestion ? STANDARD_VALUE_COLUMN_WIDTH : READ_ONLY_VALUE_COLUMN_WIDTH
);

const getPropertyPartsTableMinWidth = ({
  showBuiltValueColumn,
  showZabanColumn,
  hasPre1997ConstructionQuestion
}) => (
  PROPERTY_PARTS_TABLE_BASE_MIN_WIDTH
  + (showBuiltValueColumn ? STANDARD_VALUE_COLUMN_WIDTH : 0)
  + (showZabanColumn ? getZabanColumnWidth(hasPre1997ConstructionQuestion) : 0)
  + DEPRECIATION_COLUMN_WIDTH
);

const getPropertyPartsColumnCount = ({ showBuiltValueColumn, showZabanColumn }) => (
  5
  + (showBuiltValueColumn ? 1 : 0)
  + (showZabanColumn ? 1 : 0)
  + 1
);

const HaklaiPropertySection = React.memo(function HaklaiPropertySection({
  classes,
  sectionId,
  sectionTitle,
  omitTopFrameOnFirstBody = false,
  addMode,
  addAriaLabel,
  onAddClick,
  showAddButton = true,
  bodies,
  renderRow,
  showBuiltValueColumn,
  showZabanColumn
}) {
  const optionalResidentialColumnCount =
    (showBuiltValueColumn ? 1 : 0)
    + (showZabanColumn ? 1 : 0);
  const columnCount = getPropertyPartsColumnCount({ showBuiltValueColumn, showZabanColumn });

  return (
    <>
      {bodies.map((body, bodyIndex) => (
        <TableBody
          key={`${sectionId}_${body.bodyKey}`}
          className={[
            classes.sectionFrameBody,
            bodyIndex > 0 || omitTopFrameOnFirstBody ? classes.sectionFrameBodyNoTop : ""
          ].filter(Boolean).join(" ")}
        >
          {bodyIndex === 0 ? (
            <TableRow>
              <StyledGroupCell
                align="center"
                className={[
                  classes.groupHeaderSpacerCell,
                  classes.addColumnDividerCell
                ].join(" ")}
                style={{ width: ADD_ROW_COLUMN_WIDTH, minWidth: ADD_ROW_COLUMN_WIDTH }}
              >
                <Box className={classes.groupHeaderSpacer} />
              </StyledGroupCell>

              <StyledGroupCell align="center" className={classes.groupHeadLeadCell}>
                <Typography
                  variant="body2"
                  component="div"
                  dir="rtl"
                  className={classes.groupHeaderLeadTitle}
                >
                  {sectionTitle}
                </Typography>
              </StyledGroupCell>

              <StyledGroupCell align="right" className={classes.groupHeaderSpacerCell}>
                <Box className={classes.groupHeaderSpacer} />
              </StyledGroupCell>

              <StyledGroupCell align="right" className={classes.groupHeaderSpacerCell}>
                <Box className={classes.groupHeaderSpacer} />
              </StyledGroupCell>

              {optionalResidentialColumnCount > 0 ? (
                <StyledGroupCell
                  colSpan={optionalResidentialColumnCount}
                  align="right"
                  className={classes.groupHeaderSpacerCell}
                >
                  <Box className={classes.groupHeaderSpacer} />
                </StyledGroupCell>
              ) : null}

              <StyledGroupCell align="right" className={classes.groupHeaderSpacerCell}>
                <Box className={classes.groupHeaderSpacer} />
              </StyledGroupCell>

              <StyledGroupCell align="center" className={classes.groupHeadActionCell}>
                <Box className={classes.groupHeaderActionWrap} />
              </StyledGroupCell>
            </TableRow>
          ) : null}

          {(body.rows || []).length === 0 ? (
            <TableRow>
              <StyledGroupCell colSpan={columnCount} className={classes.emptyStateCell}>
                <Typography component="span" className={classes.emptyStateText}>
                  {HAKLAI_PROPERTY_PART_LABELS.emptySection}
                </Typography>
              </StyledGroupCell>
            </TableRow>
          ) : (body.rows || []).map(([rowKey, part], rowIndex) => renderRow(rowKey, part, {
              subSectionLabel: body.subSectionLabel,
              subSectionRowSpan: body.rows.length,
              isFirstInSubSection: rowIndex === 0,
              isLastInBody: rowIndex === body.rows.length - 1,
              showAddButton: (
                showAddButton
                && bodyIndex === bodies.length - 1
                && rowIndex === body.rows.length - 1
              ),
              addMode,
              addAriaLabel,
              onAddClick
            }))}
        </TableBody>
      ))}
    </>
  );
});

const HaklaiPropertyPartsTable = ({
  classes,
  stickyHeaderEnabled,
  showBuiltValueColumn,
  showZabanColumn,
  hasPre1997ConstructionQuestion,
  sections
}) => {
  const zabanColumnWidth = getZabanColumnWidth(hasPre1997ConstructionQuestion);
  const tableWidth = getPropertyPartsTableMinWidth({
    showBuiltValueColumn,
    showZabanColumn,
    hasPre1997ConstructionQuestion
  });
  const tableFrameRef = React.useRef(null);
  const [isTableScrollContained, setIsTableScrollContained] = React.useState(false);
  React.useEffect(() => {
    const node = tableFrameRef.current;
    if (!node) return undefined;

    const updateScrollContainment = () => {
      const availableWidth = node.clientWidth || 0;
      const shouldContain = availableWidth > 0 && availableWidth < tableWidth + FLOATING_TOOLBAR_SAFE_GUTTER;
      setIsTableScrollContained((prev) => (prev === shouldContain ? prev : shouldContain));
    };

    updateScrollContainment();
    const ResizeObserverImpl = window.ResizeObserver;
    const resizeObserver = ResizeObserverImpl ? new ResizeObserverImpl(updateScrollContainment) : null;
    if (resizeObserver) resizeObserver.observe(node);
    window.addEventListener("resize", updateScrollContainment);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", updateScrollContainment);
    };
  }, [tableWidth]);

  return (
    <>
      <Box ref={tableFrameRef} className={classes.tableFrameAlign}>
        <TableContainer
          className={[
            classes.tableWrapper,
            isTableScrollContained ? classes.tableWrapperScrollable : ""
          ].filter(Boolean).join(" ")}
        >
          <Table
            size="small"
            style={{
              tableLayout: "fixed",
              width: "100%",
              minWidth: tableWidth,
              borderCollapse: "collapse",
              backgroundColor: "#fff"
            }}
          >
        <TableHead
          className={[
            classes.stickyHeader,
            (!stickyHeaderEnabled || isTableScrollContained) ? classes.stickyHeaderDisabled : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <TableRow>
            <StyledHeaderCell
              className={[
                classes.propertyTableHeaderCell,
                classes.addColumnDividerCell
              ].join(" ")}
              style={{ width: ADD_ROW_COLUMN_WIDTH, minWidth: ADD_ROW_COLUMN_WIDTH }}
            />

            <StyledNameHeaderCell
              className={classes.propertyTableHeaderCell}
              style={{
                width: PROPERTY_PART_NAME_COLUMN_WIDTH,
                minWidth: PROPERTY_PART_NAME_COLUMN_WIDTH,
                maxWidth: PROPERTY_PART_NAME_COLUMN_WIDTH
              }}
            >
              <Typography component="span" className={classes.headerLabelText}>
                {HAKLAI_PROPERTY_PART_LABELS.farmAssets}
              </Typography>
            </StyledNameHeaderCell>

            <StyledHeaderCell
              className={[
                classes.propertyTableHeaderCell,
                classes.propertyTableTrailingHeaderCell,
              ].join(" ")}
              style={{ width: PTOR_COLUMN_WIDTH }}
              aria-label={HAKLAI_PROPERTY_PART_LABELS.requestedPtor}
            >
              <Tooltip title={HAKLAI_PROPERTY_PART_TOOLTIPS.requestedPtorForPart}>
                <Typography component="span" className={classes.headerLabelText}>
                  {HAKLAI_PROPERTY_PART_LABELS.requestedPtor}
                </Typography>
              </Tooltip>
            </StyledHeaderCell>

            <StyledHeaderCell
              className={[
                classes.propertyTableHeaderCell,
                classes.propertyTableGroupBoundaryHeaderCell,
                (!showBuiltValueColumn && !showZabanColumn) ? classes.propertyTableGroupEndHeaderCell : ""
              ].join(" ")}
              style={{ width: STANDARD_VALUE_COLUMN_WIDTH }}
            >
              <Typography component="span" className={classes.headerLabelText}>
                {HAKLAI_PROPERTY_PART_LABELS.relativeSaleValue}
              </Typography>
            </StyledHeaderCell>

            {showBuiltValueColumn ? (
              <StyledHeaderCell
                className={[
                  classes.propertyTableHeaderCell,
                  classes.propertyTableTrailingHeaderCell,
                  showZabanColumn ? "" : classes.propertyTableGroupEndHeaderCell
                ].join(" ")}
                style={{ width: STANDARD_VALUE_COLUMN_WIDTH }}
              >
                <Typography component="span" className={classes.headerLabelText}>
                  {HAKLAI_PROPERTY_PART_LABELS.builtValue}
                </Typography>
              </StyledHeaderCell>
            ) : null}

            {showZabanColumn ? (
              <StyledHeaderCell
                className={[
                  classes.propertyTableHeaderCell,
                  classes.propertyTableTrailingHeaderCell,
                  classes.propertyTableGroupEndHeaderCell
                ].join(" ")}
                style={{ width: zabanColumnWidth }}
              >
                <Tooltip title={HAKLAI_PROPERTY_PART_TOOLTIPS.autoCalculatedWhenZabanSelected}>
                  <Typography component="span" className={classes.headerLabelText}>
                    {HAKLAI_PROPERTY_PART_LABELS.zabanValue}
                  </Typography>
                </Tooltip>
              </StyledHeaderCell>
            ) : null}

            <StyledHeaderCell
              className={[
                classes.propertyTableHeaderCell,
                classes.propertyTableGroupEndHeaderCell,
                classes.propertyTableTrailingHeaderCell,
              ].join(" ")}
              style={{ width: DEPRECIATION_COLUMN_WIDTH }}
              aria-label={HAKLAI_PROPERTY_PART_LABELS.rentalDepreciation}
            >
              <Tooltip title={HAKLAI_PROPERTY_PART_TOOLTIPS.rentalDepreciationForPart}>
                <Typography component="span" className={classes.headerLabelText}>
                  {HAKLAI_PROPERTY_PART_LABELS.rentalDepreciation}
                </Typography>
              </Tooltip>
            </StyledHeaderCell>

            <StyledHeaderCell
              className={[
                classes.propertyTableHeaderCell,
                classes.propertyTableTrailingHeaderCell,
              ].join(" ")}
              style={{ width: ACTION_COLUMN_WIDTH, minWidth: ACTION_COLUMN_WIDTH }}
            />
          </TableRow>
        </TableHead>

        {sections.map((section, sectionIndex) => (
          <HaklaiPropertySection
            key={section.sectionId}
            classes={classes}
            sectionId={section.sectionId}
            sectionTitle={section.sectionTitle}
            omitTopFrameOnFirstBody={sectionIndex > 0}
            addMode={section.addMode}
            addAriaLabel={section.addAriaLabel}
            onAddClick={section.onAddClick}
            showAddButton={section.showAddButton !== false}
            bodies={section.bodies}
            renderRow={section.renderRow}
            showBuiltValueColumn={showBuiltValueColumn}
            showZabanColumn={showZabanColumn}
          />
        ))}
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

export default React.memo(HaklaiPropertyPartsTable);
